import {
  HAVE_ALL_PERMISSION,
  NOT_HAVE_PERMISSION,
  ROLE_LEVEL,
  ROLE_TYPES,
} from 'constants/backend-defines.const';
import { AppRouteConst } from 'constants/route.const';
import { isEqualVal } from 'helpers/string.helper';
import { find, forEach, size, some, values } from 'lodash';
import {
  PermissionAllowModel,
  PermissionMappingModel,
} from 'models/hook/permission.model';
import { UserModel } from 'models/user/user.model';
import { useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/store-hooks';

export function usePermission() {
  const { pathname } = useLocation();
  const { userIsLogging } = useAppSelector((state) => state.user);

  const allPermission: PermissionMappingModel = useMemo(
    () => [
      {
        role: ROLE_TYPES.Admin,
        isFullPermission: true,
      },
      {
        role: ROLE_TYPES.Manager,
        permission: [
          {
            route: AppRouteConst.DASHBOARD,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.REPORT.LIST,
            isFullPermission: true, // Nếu isFullPermission = true => Tất cả các quyền trong Report
          },
          {
            route: AppRouteConst.COMPANY,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.STORE,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.BOOTH,
            allows: {
              ...HAVE_ALL_PERMISSION,
              canSeeBoothId: false,
            },
          },
          {
            route: AppRouteConst.LAYOUT_TEMPLATE.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.THEME.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.STICKER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROMOTION_CAMPAIGN,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.USER,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            isFullPermission: true,
          },
          // ================
          // Setting
          {
            route: AppRouteConst.SETTING.LIST,
            isFullPermission: true, // Nếu isFullPermission = true => Tất cả các quyền trong Setting
          },
          {
            route: AppRouteConst.SETTING.SETTING_MEMBER.LIST,
            allows: {
              ...HAVE_ALL_PERMISSION,
              canDelete: false,
              isImportant: true,
            },
          },
        ],
      },
      {
        role: ROLE_TYPES.Sale,
        permission: [
          {
            route: AppRouteConst.DASHBOARD,
            isFullPermission: true,
          },
        ],
      },

      // =======================================
      {
        role: ROLE_TYPES.Accountant,
        permission: [
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROMOTION_CAMPAIGN,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
        ],
      },
      {
        role: ROLE_TYPES.Marketing,
        permission: [
          {
            route: AppRouteConst.BOOTH,
            allows: {
              canView: true,
              canUpdate: true,
              canDelete: false,
              disableNewNotUpdate: true,
            },
          },
          {
            route: AppRouteConst.LAYOUT_TEMPLATE.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.THEME.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.STICKER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROMOTION_CAMPAIGN,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          // Setting
          {
            route: AppRouteConst.SETTING.SETTING_BOOTH_BACKGROUND.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.SETTING.SETTING_NEWS.LIST,
            isFullPermission: true,
          },
        ],
      },
      {
        role: ROLE_TYPES.ITSupport,
        permission: [
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.REPORT.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.COMPANY,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.STORE,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.BOOTH,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.LAYOUT_TEMPLATE.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.THEME.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.STICKER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROMOTION_CAMPAIGN,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.USER,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.SETTING.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.SETTING.SETTING_MEMBER.LIST,
            allows: {
              ...HAVE_ALL_PERMISSION,
              canDelete: false,
              isImportant: true,
            },
          },
        ],
      },
      // =======================================
      {
        role: ROLE_TYPES.Franchiser, // Chủ Nhượng quyền
        permission: [
          {
            route: AppRouteConst.DASHBOARD,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.USER,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            isFullPermission: true,
          },
        ],
      },
      {
        role: ROLE_TYPES.FranchiserManager,
        permission: [
          {
            route: AppRouteConst.DASHBOARD,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            allows: { canView: false, canUpdate: false },
          },
        ],
      },
      {
        role: ROLE_TYPES.BoothReceptionist,
        permission: [
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            allows: { canView: false, canUpdate: false },
          },
        ],
      },
      // =======================================
      {
        role: ROLE_TYPES.Buyer, // Chủ Nhượng quyền
        permission: [
          {
            route: AppRouteConst.DASHBOARD,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.TRANSACTION,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.BOOTH,
            allows: {
              canView: true,
              canUpdate: true,
              disableNewNotUpdate: true,
              canUpdateBoothVersion: false,
              canDelete: false,
            },
          },
          {
            route: AppRouteConst.LAYOUT_TEMPLATE.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.THEME.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.VOUCHER.LIST,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.USER,
            isFullPermission: true,
          },
          {
            route: AppRouteConst.PROFILE.PREFIX,
            isFullPermission: true,
          },
        ],
      },
    ],
    [],
  );

  const getAllowingByPathname = useCallback(
    (path: string = pathname): PermissionAllowModel => {
      const permissionData = find(allPermission, (o) =>
        isEqualVal(o?.role, userIsLogging?.role),
      );
      if (permissionData) {
        if (permissionData?.isFullPermission) {
          return { ...HAVE_ALL_PERMISSION, isFullPermission: true };
        }
        let foundAllows = {};
        forEach(permissionData?.permission, (item) => {
          if (matchPath({ path: item.route }, path) && item?.isFullPermission) {
            foundAllows = {
              ...item,
              ...HAVE_ALL_PERMISSION,
              isFullPermission: true,
            };
            return false;
          }
          if (matchPath({ path: item.route }, path) && item?.allows) {
            foundAllows = item?.allows;
            return false;
          }
        });
        return {
          ...NOT_HAVE_PERMISSION,
          ...foundAllows,
        };
      }
      return { ...NOT_HAVE_PERMISSION };
    },
    [allPermission, pathname, userIsLogging?.role],
  );

  const getFistRouteHasViewPermission = useCallback((): string => {
    const permissionData = find(allPermission, (o) =>
      isEqualVal(o?.role, userIsLogging?.role),
    );
    if (permissionData) {
      if (permissionData?.isFullPermission) {
        return AppRouteConst.DASHBOARD;
      }
      let droute = '';
      forEach(permissionData?.permission, (item) => {
        if (item?.isFullPermission || item?.allows?.canView) {
          droute = item?.route;
          return false;
        }
      });
      if (droute) return droute;
    }
    return '';
  }, [allPermission, userIsLogging?.role]);

  const compareRoleLevel = useCallback(
    (user: UserModel | null | undefined): boolean => {
      if (user?.role === ROLE_TYPES.Admin) return true;

      return !!(
        !!userIsLogging &&
        !!user?.role &&
        ROLE_LEVEL[userIsLogging?.role] > ROLE_LEVEL[user?.role]
      );
    },
    [userIsLogging],
  );

  const getRecursiveAnyPermissionRoute = useCallback(
    (
      routeObj: any,
      keyOfPermission: keyof PermissionAllowModel = 'canView',
    ): PermissionAllowModel => {
      let isFounded = false;
      let allows = NOT_HAVE_PERMISSION;

      const checkChildRoutePermission = (path: any) => {
        if (isFounded || !path) return;

        if (typeof path === 'string') {
          if (getAllowingByPathname(path)?.[keyOfPermission]) {
            isFounded = true;
            allows = Object.assign(getAllowingByPathname(path), {});
          }
          return;
        }

        if (size(path)) {
          forEach(path, (item) => checkChildRoutePermission(item));
        }
      };

      checkChildRoutePermission(routeObj);

      return allows;
    },
    [getAllowingByPathname],
  );

  const currentUserAllow = useMemo(
    () => getAllowingByPathname(),
    [getAllowingByPathname],
  );

  const isSaleRole = isEqualVal(userIsLogging?.role, ROLE_TYPES.Sale);

  return useMemo(
    () => ({
      getRecursiveAnyPermissionRoute,
      getAllowingByPathname,
      getFistRouteHasViewPermission,
      currentUserAllow,
      isSaleRole,
      compareRoleLevel,
    }),
    [
      compareRoleLevel,
      currentUserAllow,
      getAllowingByPathname,
      getFistRouteHasViewPermission,
      getRecursiveAnyPermissionRoute,
      isSaleRole,
    ],
  );
}
